import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Centrifuge } from 'centrifuge';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar as MuiAvatar,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import {
  Add,
  Search,
  VisibilityOutlined,
  Edit,
  PersonAdd,
  ExitToApp,
  Archive,
  ExpandMore,
  MoreVert,
  Delete as DeleteIcon,
  Create as CreateIcon,
  Notifications,
  NotificationsOff,
} from '@material-ui/icons';

import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { TooltipMenu } from '../../../common/tooltip-menu';
import { useSession } from '../../../global/auth/useSession';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { CreateChannelDialog } from './CreateChannelDialog';
import { EditChannelDialog } from './EditChannelDialog';
import { ManageMembersDialog } from './ManageMembersDialog';
import { ReactionChips, ReactionPickerButton } from './MessageReactions';
import { MessageTypeMenu, MessageTypeAction } from './MessageTypeMenu';
import { PollDialog } from './message-types/PollDialog';
import { ShareActivityDialog } from './message-types/ShareActivityDialog';
import { ShareLocationDialog } from './message-types/ShareLocationDialog';
import { ImpactShareDialog } from './message-types/ImpactShareDialog';
import { ShareRosterCheckinDialog } from './message-types/ShareRosterCheckinDialog';
import { ShareResourceDialog } from './message-types/ShareResourceDialog';
import { RichMessageCard } from './message-types/RichMessageCard';
import { FallbackAvatar } from '../user-profile/components/Avatar';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'https://chat.volaby.org';

const URL_REGEX = /(\b(?:https?:\/\/|www\.)[^\s<>"']+)/gi;
const TRAILING_PUNCT = /[)\].,;:!?'"]+$/;

const linkify = (text: string, isOwn: boolean): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = URL_REGEX.exec(text)) !== null) {
    let url = match[0];
    let trailing = '';
    const punct = url.match(TRAILING_PUNCT);
    if (punct) {
      [trailing] = punct;
      url = url.slice(0, -trailing.length);
    }
    const href = url.startsWith('www.') ? `https://${url}` : url;
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={`${match.index}-${url}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={{
          color: isOwn ? '#fff' : '#1976d2',
          textDecoration: 'underline',
          wordBreak: 'break-all',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : [text];
};

const MegaphoneIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg viewBox='0 0 24 24' width='1em' height='1em' fill='currentColor' style={style}>
    <path d='M3 11v2a2 2 0 0 0 2 2h1l2 5h2l-2-5h1l7 4V5L9 9H5a2 2 0 0 0-2 2zm16-2v6a3 3 0 0 0 0-6z' />
  </svg>
);

interface Conversation {
  id: string;
  name: string;
  type: string;
  lastMessage: { content: string; volunteerId: string; createdAt: string } | null;
  participantCount: number;
  unreadCount: number;
  myRole: string;
  updatedAt: string;
  isBroadcast?: boolean;
}

interface Message {
  id: string;
  conversationId: string;
  volunteerId: string;
  content: string;
  metadata?: Record<string, any>;
  createdAt: string;
  isEdited: boolean;
  isDeleted?: boolean;
  isPinned?: boolean;
  reactions: Array<{ emoji: string; volunteerId: string }>;
}

interface ProfileInfo {
  preferredName: string;
  lastName: string;
  avatarUrl: string | null;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
    },
    convList: {
      width: 340,
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      background: '#fafafa',
    },
    convListMobile: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#fafafa',
    },
    searchBox: {
      padding: '12px 16px',
      borderBottom: '1px solid #e0e0e0',
    },
    convItem: {
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0',
      '&:hover': { background: '#f0f0f0' },
    },
    convItemActive: {
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0',
      background: '#e3f2fd',
    },
    msgArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    msgHeader: {
      padding: '12px 20px',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    },
    msgList: {
      flex: 1,
      overflowY: 'auto',
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
    },
    msgRow: {
      display: 'flex',
      gap: 4,
      alignItems: 'flex-end',
      '& .msg-actions': {
        opacity: 0,
        transition: 'opacity 0.15s ease',
      },
      '&:hover .msg-actions': {
        opacity: 1,
      },
    },
    msgActionBtn: {
      padding: 4,
      color: '#9e9e9e',
      '&:hover': {
        background: 'rgba(0,0,0,0.04)',
        color: '#616161',
      },
    },
    msgRowOwn: {
      maxWidth: '70%',
      alignSelf: 'flex-end',
    },
    msgRowOther: {
      maxWidth: '70%',
      alignSelf: 'flex-start',
    },
    msgBubble: {
      padding: '8px 14px',
      borderRadius: 16,
      fontSize: '0.9rem',
      lineHeight: 1.4,
    },
    msgOwn: {
      background: '#1976d2',
      color: '#fff',
      borderBottomRightRadius: 4,
    },
    msgOther: {
      background: '#f0f0f0',
      color: '#333',
      borderBottomLeftRadius: 4,
    },
    msgOwnContinued: {
      background: '#1976d2',
      color: '#fff',
      borderBottomRightRadius: 4,
      borderTopRightRadius: 4,
    },
    msgOtherContinued: {
      background: '#f0f0f0',
      color: '#333',
      borderBottomLeftRadius: 4,
      borderTopLeftRadius: 4,
    },
    inputArea: {
      padding: '12px 20px 20px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      gap: 8,
      alignItems: 'center',
    },
    sendButton: {
      color: '#7c4dff',
      padding: 8,
      '&:hover': {
        background: 'transparent',
        color: '#651fff',
      },
      '&.Mui-disabled': {
        color: '#ccc',
      },
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999',
    },
    menuAction: {
      color: '#e0e0e0',
      display: 'flex',
      alignItems: 'center',
      paddingTop: 10,
      justifyContent: 'flex-start',
      textTransform: 'none',
      width: '100%',
      '& p': { fontSize: '14px' },
      '& svg': { fontSize: '16px' },
    },
    menuActionDanger: {
      color: '#ef9a9a',
      display: 'flex',
      alignItems: 'center',
      paddingTop: 10,
      justifyContent: 'flex-start',
      textTransform: 'none',
      width: '100%',
      '& p': { fontSize: '14px' },
      '& svg': { fontSize: '16px' },
    },
  })
);

const getInitials = (name: string): string => {
  return name
    .split(/[\s-]+/)
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24 && date.getDate() === now.getDate()) return `${diffHours}h ago`;
  if (diffHours < 48) return 'Yesterday';
  if (diffHours < 168) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatMessageTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const PinIconSmall = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M15 4.5l-4 4l-4 1.5l-1.5 1.5l7 7l1.5 -1.5l1.5 -4l4 -4' />
    <line x1='9' y1='15' x2='4.5' y2='19.5' />
    <line x1='14.5' y1='4' x2='20' y2='9.5' />
  </svg>
);

const shouldShowTimestamp = (current: Message, next: Message | undefined): boolean => {
  if (!next) return true;
  if (current.volunteerId !== next.volunteerId) return true;
  const diff = new Date(next.createdAt).getTime() - new Date(current.createdAt).getTime();
  return diff > 5 * 60 * 1000;
};

const isNewGroup = (current: Message, prev: Message | undefined): boolean => {
  if (!prev) return true;
  if (current.volunteerId !== prev.volunteerId) return true;
  const diff = new Date(current.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return diff > 5 * 60 * 1000;
};

const MessageAvatar = ({ profile, size = 32 }: { profile: ProfileInfo | undefined; size?: number }) => {
  if (profile?.avatarUrl) {
    return <MuiAvatar src={profile.avatarUrl} style={{ width: size, height: size }} />;
  }

  const initials = profile ? getInitials(`${profile.preferredName} ${profile.lastName}`) : null;
  return <FallbackAvatar initials={initials} size={size} />;
};

const ChannelOptionsMenu = ({
  classes,
  conversation,
  isAdmin,
  apiUrl,
  userToken,
  chatToken,
  onLeave,
  onUpdate,
}: {
  classes: Record<string, string>;
  conversation: Conversation;
  isAdmin: boolean;
  apiUrl: string;
  userToken?: string;
  chatToken: string | null;
  onLeave: () => void;
  onUpdate: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const isCustom = conversation.type === 'custom';

  useEffect(() => {
    if (!chatToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${CHAT_API_URL}/api/conversations/${conversation.id}/notification-settings`, {
          headers: { Authorization: `JWT ${chatToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setIsMuted(Boolean(data.muted));
      } catch {
        // Ignore — default remains unmuted
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation.id, chatToken]);

  const handleToggleMute = async () => {
    if (!chatToken) return;
    setOpen(false);
    const next = !isMuted;
    setIsMuted(next);
    try {
      await fetch(`${CHAT_API_URL}/api/conversations/${conversation.id}/notification-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `JWT ${chatToken}` },
        body: JSON.stringify({ muted: next }),
      });
    } catch {
      setIsMuted(!next);
    }
  };
  const managedTooltip = `This channel is managed in ${
    conversation.type === 'activity'
      ? 'Activity Management'
      : conversation.type === 'program'
      ? 'Program Management'
      : 'Role Management'
  }`;

  const callEndpoint = async (path: string, body: Record<string, unknown>) => {
    if (!userToken) return;
    await fetch(`${apiUrl}/vm/chat/channel/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `JWT ${userToken}` },
      body: JSON.stringify(body),
    });
  };

  const handleLeave = async () => {
    setOpen(false);
    await callEndpoint('leave', { conversationId: conversation.id });
    onLeave();
  };

  const handleArchive = async () => {
    setOpen(false);
    await callEndpoint('archive', { conversationId: conversation.id });
    onLeave();
  };

  const handleToggleBroadcast = async () => {
    if (!chatToken) return;
    setOpen(false);
    await fetch(`${CHAT_API_URL}/api/conversations/${conversation.id}/broadcast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `JWT ${chatToken}` },
      body: JSON.stringify({ isBroadcast: !conversation.isBroadcast }),
    });
    onUpdate();
  };

  return (
    <>
      <TooltipMenu
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '4px 8px' }}>
            {isAdmin && isCustom && (
              <Button
                className={classes.menuAction}
                startIcon={<Edit style={{ fontSize: 16 }} />}
                onClick={() => {
                  setOpen(false);
                  setShowEditDialog(true);
                }}
              >
                <Typography>Edit Channel</Typography>
              </Button>
            )}
            {isAdmin && !isCustom && (
              <Button
                className={classes.menuAction}
                style={{ opacity: 0.4, cursor: 'default' }}
                startIcon={<Edit style={{ fontSize: 16 }} />}
                title={managedTooltip}
              >
                <Typography>Edit Channel</Typography>
              </Button>
            )}
            {isAdmin && (
              <Button
                className={classes.menuAction}
                startIcon={<PersonAdd style={{ fontSize: 16 }} />}
                onClick={() => {
                  setOpen(false);
                  setShowMembersDialog(true);
                }}
              >
                <Typography>Manage Members</Typography>
              </Button>
            )}
            {isAdmin && (
              <Button
                className={classes.menuAction}
                startIcon={<MegaphoneIcon style={{ fontSize: 16 }} />}
                onClick={handleToggleBroadcast}
              >
                <Typography>{conversation.isBroadcast ? 'Disable Broadcast' : 'Enable Broadcast'}</Typography>
              </Button>
            )}
            <Button
              className={classes.menuAction}
              startIcon={
                isMuted ? <Notifications style={{ fontSize: 16 }} /> : <NotificationsOff style={{ fontSize: 16 }} />
              }
              onClick={handleToggleMute}
            >
              <Typography>{isMuted ? 'Unmute Notifications' : 'Mute Notifications'}</Typography>
            </Button>
            {isCustom ? (
              <Button
                className={classes.menuAction}
                startIcon={<ExitToApp style={{ fontSize: 16 }} />}
                onClick={handleLeave}
              >
                <Typography>Leave Channel</Typography>
              </Button>
            ) : (
              <Button
                className={classes.menuAction}
                style={{ opacity: 0.4, cursor: 'default' }}
                startIcon={<ExitToApp style={{ fontSize: 16 }} />}
                title={managedTooltip}
              >
                <Typography>Leave Channel</Typography>
              </Button>
            )}
            {isAdmin && isCustom && (
              <Button
                className={classes.menuActionDanger}
                startIcon={<Archive style={{ fontSize: 16 }} />}
                onClick={handleArchive}
              >
                <Typography>Archive Channel</Typography>
              </Button>
            )}
            {isAdmin && !isCustom && (
              <Button
                className={classes.menuActionDanger}
                style={{ opacity: 0.4, cursor: 'default' }}
                startIcon={<Archive style={{ fontSize: 16 }} />}
                title={managedTooltip}
              >
                <Typography>Archive Channel</Typography>
              </Button>
            )}
          </Box>
        }
      >
        <TabletButton
          size='small'
          variant='outlined'
          onClick={() => setOpen(true)}
          endIcon={<ExpandMore />}
          style={{
            marginRight: 8,
            borderRadius: 6,
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            padding: '4px 12px',
            border: '1px solid #ccc',
            color: '#666',
          }}
        >
          Options
        </TabletButton>
      </TooltipMenu>

      <EditChannelDialog
        open={showEditDialog}
        conversationId={conversation.id}
        currentName={conversation.name}
        onClose={() => setShowEditDialog(false)}
        onUpdated={onUpdate}
      />

      <ManageMembersDialog
        open={showMembersDialog}
        conversationId={conversation.id}
        chatToken={chatToken}
        onClose={() => setShowMembersDialog(false)}
        onUpdated={onUpdate}
        readOnly={!isCustom}
      />
    </>
  );
};

export const ChatScreen = () => {
  const classes = useStyles();
  const { user } = useSession();
  const apiUrl = useApiUrl();
  const { isMobile } = useCampfireTheme();

  const [chatToken, setChatToken] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [openMsgMenuId, setOpenMsgMenuId] = useState<string | null>(null);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [viewAllChannels, setViewAllChannels] = useState(false);
  const [viewPinned, setViewPinned] = useState(false);
  const isAdmin = user?.userIdentityService?.isVmAdmin;
  const currentUserId = user?.userIdentity?.vm?.volunteerIdentity?.volunteerId;
  const [profileCache, setProfileCache] = useState<Record<string, ProfileInfo>>(() => {
    try {
      const stored = localStorage.getItem('volaby-chat-profiles');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const profileCacheRef = useRef(profileCache);
  profileCacheRef.current = profileCache;

  useEffect(() => {
    try {
      localStorage.setItem('volaby-chat-profiles', JSON.stringify(profileCache));
    } catch {
      /* ignore */
    }
  }, [profileCache]);
  const msgListRef = useRef<HTMLDivElement>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isPrependingRef = useRef(false);
  const preservedScrollHeightRef = useRef<number | null>(null);

  const chatFetch = useCallback(
    async (path: string, options: RequestInit = {}) => {
      if (!chatToken) return null;
      const res = await fetch(`${CHAT_API_URL}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${chatToken}`,
          ...options.headers,
        },
      });
      if (!res.ok) throw new Error(`Chat API error: ${res.status}`);
      return res.json();
    },
    [chatToken]
  );

  useEffect(() => {
    const w = window as any;
    const hideKey = 'hide_default_launcher';
    if (w.Intercom) {
      w.Intercom('update', { [hideKey]: true });
    }
    return () => {
      if (w.Intercom) {
        w.Intercom('update', { [hideKey]: false });
      }
    };
  }, []);

  useEffect(() => {
    if (!chatToken) return undefined;
    const pingAway = (isAway: boolean) => {
      fetch(`${CHAT_API_URL}/api/presence/away`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `JWT ${chatToken}` },
        body: JSON.stringify({ isAway }),
      }).catch(() => {
        // best-effort
      });
    };
    const onVisibilityChange = () => pingAway(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (document.hidden) pingAway(true);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      pingAway(false);
    };
  }, [chatToken]);

  useEffect(() => {
    if (!user?.token) return;

    const fetchChatToken = async () => {
      try {
        const res = await fetch(`${apiUrl}/vm/chat/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `JWT ${user.token}`,
          },
        });
        const data = await res.json();
        if (data.data?.chatToken) {
          setChatToken(data.data.chatToken);
        }
      } catch (err) {
        console.error('Failed to get chat token:', err);
      }
    };

    fetchChatToken();
  }, [user?.token, apiUrl]);

  const centrifugeRef = useRef<Centrifuge | null>(null);

  useEffect(() => {
    if (!chatToken) return undefined;

    const client = new Centrifuge(`wss://${new URL(CHAT_API_URL).host}/connection/websocket`, {
      getToken: async () => {
        const data = await chatFetch('/api/conversations/connection-token');
        return data?.token || '';
      },
    });

    client.connect();
    centrifugeRef.current = client;

    return () => {
      client.disconnect();
      centrifugeRef.current = null;
    };
  }, [chatToken]);

  useEffect(() => {
    if (!chatToken) return;

    const loadConversations = async () => {
      try {
        const endpoint = viewAllChannels ? '/api/conversations/all' : '/api/conversations';
        const data = await chatFetch(endpoint);
        setConversations(data || []);

        const senderIds = (data || [])
          .filter((c: Conversation) => c.lastMessage)
          .map((c: Conversation) => c.lastMessage!.volunteerId)
          .filter((id: string) => !profileCache[id]);
        const uniqueIds = [...new Set(senderIds)] as string[];

        if (uniqueIds.length > 0 && user?.token) {
          const res = await fetch(`${apiUrl}/vm/chat/profiles`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `JWT ${user.token}` },
            body: JSON.stringify({ volunteerIds: uniqueIds }),
          });
          const profileData = await res.json();
          if (profileData.data?.profiles) {
            setProfileCache((prev) => {
              const updated = { ...prev };
              profileData.data.profiles.forEach((p: ProfileInfo & { volunteerId: string }) => {
                updated[p.volunteerId] = p;
              });
              return updated;
            });
          }
        }
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [chatToken, chatFetch, viewAllChannels]);

  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;

  useEffect(() => {
    const client = centrifugeRef.current;
    if (!client || conversations.length === 0 || !chatToken) return undefined;

    const subs: any[] = [];

    conversations.forEach((conv) => {
      const channel = `chat:conversation_${conv.id}`;

      const sub = client.newSubscription(channel, {
        getToken: async () => {
          const data = await chatFetch(`/api/conversations/${conv.id}/token`);
          return data?.token || '';
        },
      });

      sub.on('publication', (ctx: any) => {
        if (ctx.data?.type === 'message') {
          const msg = ctx.data.message;
          const isActive = activeConversationRef.current?.id === conv.id;

          if (isActive) {
            setMessages((prev) => {
              if (prev.some((m: Message) => m.id === msg.id)) return prev;
              return [...prev, { ...msg, reactions: msg.reactions ?? [] }];
            });
          }

          setConversations((prev) =>
            prev.map((c) => {
              if (c.id !== conv.id) return c;
              return {
                ...c,
                lastMessage: { content: msg.content, volunteerId: msg.volunteerId, createdAt: msg.createdAt },
                unreadCount: isActive ? 0 : c.unreadCount + 1,
                updatedAt: msg.createdAt,
              };
            })
          );
        }

        if (ctx.data?.type === 'poll_update') {
          setMessages((prev) =>
            prev.map((m) => (m.id === ctx.data.messageId ? { ...m, metadata: ctx.data.metadata } : m))
          );
        }

        if (ctx.data?.type === 'message_edited') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === ctx.data.message.id ? { ...m, content: ctx.data.message.content, isEdited: true } : m
            )
          );
        }

        if (ctx.data?.type === 'message_deleted') {
          if (ctx.data.deleteType === 'admin') {
            setMessages((prev) => prev.filter((m) => m.id !== ctx.data.messageId));
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === ctx.data.messageId ? { ...m, isDeleted: true, metadata: { type: 'owner_deleted' } } : m
              )
            );
          }
        }

        if (ctx.data?.type === 'message_pinned') {
          setMessages((prev) =>
            prev.map((m) => (m.id === ctx.data.messageId ? { ...m, isPinned: ctx.data.isPinned } : m))
          );
        }

        if (ctx.data?.type === 'broadcast_changed') {
          setConversations((prev) =>
            prev.map((c) => (c.id === conv.id ? { ...c, isBroadcast: !!ctx.data.isBroadcast } : c))
          );
          setActiveConversation((curr) =>
            curr && curr.id === conv.id ? { ...curr, isBroadcast: !!ctx.data.isBroadcast } : curr
          );
        }

        if (ctx.data?.type === 'reaction') {
          const { messageId, volunteerId, emoji, action } = ctx.data;
          if (volunteerId === currentUserId) return;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== messageId) return m;
              if (action === 'added') {
                const exists = m.reactions.some((r) => r.emoji === emoji && r.volunteerId === volunteerId);
                return exists ? m : { ...m, reactions: [...m.reactions, { emoji, volunteerId }] };
              }
              return {
                ...m,
                reactions: m.reactions.filter((r) => !(r.emoji === emoji && r.volunteerId === volunteerId)),
              };
            })
          );
        }
      });

      sub.subscribe();
      subs.push(sub);
    });

    return () => {
      subs.forEach((sub) => {
        sub.unsubscribe();
        client.removeSubscription(sub);
      });
    };
  }, [conversations.length, chatToken, centrifugeRef.current]);

  useEffect(() => {
    setMessages([]);
    setHasMoreMessages(false);
    setIsLoadingMore(false);
    if (!activeConversation || !chatToken) return;

    const loadMessages = async () => {
      try {
        const data = await chatFetch(`/api/messages/${activeConversation.id}`);
        setMessages(data?.messages || []);
        setHasMoreMessages(!!data?.hasMore);

        if (data?.messages?.length > 0) {
          const lastMsg = data.messages[data.messages.length - 1];
          chatFetch(`/api/messages/${activeConversation.id}/read`, {
            method: 'POST',
            body: JSON.stringify({ messageId: lastMsg.id }),
          }).catch(() => {});

          setConversations((prev) => prev.map((c) => (c.id === activeConversation.id ? { ...c, unreadCount: 0 } : c)));
        }
      } catch (err) {
        console.error('Failed to load messages:', err);
      }
    };

    loadMessages();
  }, [activeConversation, chatToken, chatFetch]);

  const loadMoreMessages = useCallback(async () => {
    if (!activeConversation || !chatToken || isLoadingMore || !hasMoreMessages || messages.length === 0) return;
    const earliest = messages[0];
    setIsLoadingMore(true);
    preservedScrollHeightRef.current = msgListRef.current?.scrollHeight ?? null;
    isPrependingRef.current = true;
    try {
      const data = await chatFetch(
        `/api/messages/${activeConversation.id}?before=${encodeURIComponent(earliest.createdAt)}`
      );
      if (data?.messages?.length) {
        setMessages((prev) => [...data.messages, ...prev]);
      }
      setHasMoreMessages(!!data?.hasMore);
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeConversation, chatToken, chatFetch, isLoadingMore, hasMoreMessages, messages]);

  useEffect(() => {
    if (!user?.token || messages.length === 0) return;

    const unknownIds = [...new Set(messages.map((m) => m.volunteerId))].filter((id) => !profileCacheRef.current[id]);
    if (unknownIds.length === 0) return;

    const fetchProfiles = async () => {
      try {
        const res = await fetch(`${apiUrl}/vm/chat/profiles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `JWT ${user.token}`,
          },
          body: JSON.stringify({ volunteerIds: unknownIds }),
        });
        const data = await res.json();
        if (data.data?.profiles) {
          setProfileCache((prev) => {
            const updated = { ...prev };
            data.data.profiles.forEach((p: ProfileInfo & { volunteerId: string }) => {
              updated[p.volunteerId] = p;
            });
            return updated;
          });
        }
      } catch (err) {
        console.error('Failed to fetch profiles:', err);
      }
    };

    fetchProfiles();
  }, [messages, user?.token, apiUrl]);

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (!msgListRef.current) {
      prevMessageCountRef.current = messages.length;
      return;
    }
    if (isPrependingRef.current) {
      const savedHeight = preservedScrollHeightRef.current;
      if (savedHeight != null) {
        const newHeight = msgListRef.current.scrollHeight;
        msgListRef.current.scrollTop += newHeight - savedHeight;
      }
      isPrependingRef.current = false;
      preservedScrollHeightRef.current = null;
    } else if (messages.length > prevMessageCountRef.current) {
      msgListRef.current.scrollTop = msgListRef.current.scrollHeight;
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const [activeMessageType, setActiveMessageType] = useState<MessageTypeAction | null>(null);
  const [pendingPhoto, setPendingPhoto] = useState<{ file: File; preview: string } | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileAttachInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setPendingPhoto({ file, preview });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePendingPhoto = () => {
    if (pendingPhoto) {
      URL.revokeObjectURL(pendingPhoto.preview);
      setPendingPhoto(null);
    }
  };

  const uploadAndSendPhoto = async () => {
    if (!pendingPhoto || !user?.token || !activeConversation) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', pendingPhoto.file);

    try {
      const res = await fetch(`${apiUrl}/vm/chat/upload`, {
        method: 'POST',
        headers: { Authorization: `JWT ${user.token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.data?.imageUrl) {
        const content = inputText.trim() || 'Sent a photo';
        await handleRichMessageSend(content, { type: 'photo', imageUrl: data.data.imageUrl });
        setInputText('');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      URL.revokeObjectURL(pendingPhoto.preview);
      setPendingPhoto(null);
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!activeConversation || !user?.token) return;
    await fetch(`${apiUrl}/vm/chat/message/delete`, {
      method: 'POST',
      headers: {
        Authorization: `JWT ${user.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        conversationId: activeConversation.id,
        messageId,
      }),
    });
  };

  const handleToggleReaction = async (messageId: string, emoji: string) => {
    if (!chatToken || !currentUserId) return;
    const hadMine = messages
      .find((m) => m.id === messageId)
      ?.reactions.some((r) => r.emoji === emoji && r.volunteerId === currentUserId);
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const others = m.reactions.filter((r) => !(r.emoji === emoji && r.volunteerId === currentUserId));
        return { ...m, reactions: hadMine ? others : [...others, { emoji, volunteerId: currentUserId }] };
      })
    );
    try {
      await fetch(`${CHAT_API_URL}/api/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: { Authorization: `JWT ${chatToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
    } catch (err) {
      console.error('Failed to toggle reaction', err);
    }
  };

  const handlePinMessage = async (messageId: string) => {
    if (!activeConversation || !chatToken) return;
    await fetch(`${CHAT_API_URL}/api/messages/${activeConversation.id}/${messageId}/pin`, {
      method: 'POST',
      headers: {
        Authorization: `JWT ${chatToken}`,
        'Content-Type': 'application/json',
      },
    });
  };

  const handleEditMessage = async (messageId: string) => {
    if (!activeConversation || !chatToken || !editText.trim()) return;
    await fetch(`${CHAT_API_URL}/api/messages/${activeConversation.id}/${messageId}`, {
      method: 'PUT',
      headers: {
        Authorization: `JWT ${chatToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content: editText.trim() }),
    });
    setEditingMsgId(null);
    setEditText('');
  };

  const canEditMessage = (msg: Message): boolean => {
    const isOwn = msg.volunteerId === currentUserId;
    if (!isOwn) return false;
    if (msg.metadata?.type) return false;
    if (isAdmin) return true;
    const ageMs = Date.now() - new Date(msg.createdAt).getTime();
    return ageMs < 5 * 60 * 1000;
  };

  const handleFileAttachClick = () => {
    if (fileAttachInputRef.current) {
      fileAttachInputRef.current.click();
    }
  };

  const handleFileAttachSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    if (fileAttachInputRef.current) fileAttachInputRef.current.value = '';
  };

  const handleRemovePendingFile = () => {
    setPendingFile(null);
  };

  const uploadAndSendFile = async () => {
    if (!pendingFile || !user?.token || !activeConversation) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', pendingFile);

    try {
      const res = await fetch(`${apiUrl}/vm/chat/upload-file`, {
        method: 'POST',
        headers: { Authorization: `JWT ${user.token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.data?.fileUrl) {
        const content = inputText.trim() || 'Sent a file';
        await handleRichMessageSend(content, {
          type: 'file',
          fileUrl: data.data.fileUrl,
          fileName: data.data.fileName,
          fileSize: data.data.fileSize,
        });
        setInputText('');
      }
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setPendingFile(null);
      setUploading(false);
    }
  };

  const handleMessageTypeSelect = (type: MessageTypeAction) => {
    setActiveMessageType(type);
  };

  const handleVote = async (messageId: string, option: string) => {
    if (!activeConversation) return;
    try {
      const result = await chatFetch(`/api/messages/${activeConversation.id}/${messageId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ option }),
      });
      if (result?.metadata) {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, metadata: result.metadata } : m)));
      }
    } catch (err) {
      console.error('Failed to vote:', err);
    }
  };

  const handleRichMessageSend = async (content: string, metadata: Record<string, unknown>) => {
    if (!activeConversation || !chatToken) return;
    try {
      const newMsg = await chatFetch('/api/messages', {
        method: 'POST',
        body: JSON.stringify({ conversationId: activeConversation.id, content, metadata }),
      });
      if (newMsg) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      }
    } catch (err) {
      console.error('Failed to send rich message:', err);
    }
  };

  const handleSend = async () => {
    if (pendingPhoto) {
      await uploadAndSendPhoto();
      return;
    }

    if (pendingFile) {
      await uploadAndSendFile();
      return;
    }

    if (!inputText.trim() || !activeConversation) return;

    const content = inputText.trim();
    setInputText('');

    try {
      const newMsg = await chatFetch('/api/messages', {
        method: 'POST',
        body: JSON.stringify({ conversationId: activeConversation.id, content }),
      });
      if (newMsg) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setInputText(content);
    }
  };

  const renderPendingAttachment = () => {
    if (!pendingPhoto && !pendingFile) return null;

    const closeIcon = (
      <svg
        width='14'
        height='14'
        viewBox='0 0 24 24'
        fill='none'
        stroke='#999'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M18 6l-12 12' />
        <path d='M6 6l12 12' />
      </svg>
    );

    return (
      <Box
        display='flex'
        alignItems='center'
        px={2}
        py={1}
        style={{ borderTop: '1px solid #e0e0e0', background: '#fafafa', gap: 12 }}
      >
        {pendingPhoto && (
          <Box style={{ position: 'relative' }}>
            <img
              src={pendingPhoto.preview}
              alt='Pending upload'
              style={{ height: 60, borderRadius: 6, display: 'block' }}
            />
            <IconButton
              size='small'
              onClick={handleRemovePendingPhoto}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                padding: 2,
              }}
            >
              {closeIcon}
            </IconButton>
          </Box>
        )}
        {pendingFile && (
          <Box
            display='flex'
            alignItems='center'
            style={{ gap: 8, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '6px 12px' }}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#7c4dff'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M14 3v4a1 1 0 0 0 1 1h4' />
              <path d='M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z' />
            </svg>
            <Typography
              variant='body2'
              style={{
                fontWeight: 500,
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pendingFile.name}
            </Typography>
            <IconButton size='small' onClick={handleRemovePendingFile} style={{ padding: 2 }}>
              {closeIcon}
            </IconButton>
          </Box>
        )}
        {uploading && <CircularProgress size={20} />}
      </Box>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const filteredConversations = conversations.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const renderMessages = () => {
    const displayMessages = viewPinned ? messages.filter((m) => m.isPinned) : messages;

    return (
      <Box
        className={classes.msgList}
        ref={msgListRef}
        onScroll={(e) => {
          if (viewPinned) return;
          if (e.currentTarget.scrollTop < 80 && hasMoreMessages && !isLoadingMore) {
            loadMoreMessages();
          }
        }}
      >
        {!viewPinned && isLoadingMore && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {viewPinned && displayMessages.length === 0 && (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Typography variant='body2' style={{ color: '#999' }}>
              No pinned messages
            </Typography>
          </Box>
        )}
        {displayMessages.map((msg, idx) => {
          const isOwn = msg.volunteerId === currentUserId;
          const prev = idx > 0 ? displayMessages[idx - 1] : undefined;
          const next = idx < displayMessages.length - 1 ? displayMessages[idx + 1] : undefined;
          const startOfGroup = isNewGroup(msg, prev);
          const showTime = shouldShowTimestamp(msg, next);
          const isLastInGroup = !next || next.volunteerId !== msg.volunteerId || shouldShowTimestamp(msg, next);
          const profile = profileCache[msg.volunteerId];

          const bubbleClass = startOfGroup
            ? `${classes.msgBubble} ${isOwn ? classes.msgOwn : classes.msgOther}`
            : `${classes.msgBubble} ${isOwn ? classes.msgOwnContinued : classes.msgOtherContinued}`;

          if (msg.isDeleted || msg.metadata?.type === 'owner_deleted') {
            return (
              <React.Fragment key={msg.id}>
                <Box
                  style={{
                    marginTop: startOfGroup ? 12 : 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isOwn ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box className={`${classes.msgRow} ${isOwn ? classes.msgRowOwn : classes.msgRowOther}`}>
                    {!isOwn && (
                      <Box style={{ width: 32, flexShrink: 0, display: 'flex', alignItems: 'center', paddingRight: 8 }}>
                        {isLastInGroup && <MessageAvatar profile={profile} size={32} />}
                      </Box>
                    )}
                    <Box
                      className={classes.msgBubble}
                      style={{
                        border: '1px solid #ccc',
                        background: 'transparent',
                      }}
                    >
                      <Typography variant='body2' style={{ color: '#999', fontStyle: 'italic' }}>
                        Message removed
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={msg.id}>
              <Box
                className='msg-hover-parent'
                style={{
                  marginTop: startOfGroup ? 12 : 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isOwn ? 'flex-end' : 'flex-start',
                  position: 'relative',
                }}
              >
                {!isOwn && startOfGroup && profile && (
                  <Typography
                    variant='caption'
                    style={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: '#555',
                      marginLeft: 40,
                      marginBottom: 2,
                      display: 'block',
                    }}
                  >
                    {profile.preferredName} {profile.lastName}
                  </Typography>
                )}
                {msg.isPinned && (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginBottom: 2,
                      paddingLeft: isOwn ? 0 : 40,
                      paddingRight: isOwn ? 4 : 0,
                      justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <PinIconSmall />
                    <Typography variant='caption' style={{ color: '#999', fontSize: '0.6rem' }}>
                      Pinned
                    </Typography>
                  </Box>
                )}
                <Box
                  className={`${classes.msgRow} ${isOwn ? classes.msgRowOwn : classes.msgRowOther}`}
                  style={
                    [
                      'photo',
                      'activity_link',
                      'roster_link',
                      'checkin_link',
                      'cico_link',
                      'resource_link',
                      'report_share',
                    ].includes(msg.metadata?.type)
                      ? { maxWidth: 'none' }
                      : undefined
                  }
                >
                  {!isOwn && (
                    <Box style={{ width: 32, flexShrink: 0, display: 'flex', alignItems: 'center', paddingRight: 8 }}>
                      {isLastInGroup && <MessageAvatar profile={profile} size={32} />}
                    </Box>
                  )}
                  {isOwn && (
                    <Box
                      className='msg-actions'
                      style={{
                        flexShrink: 0,
                        alignSelf: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        ...(openMsgMenuId === msg.id ? { opacity: 1 } : {}),
                      }}
                    >
                      <TooltipMenu
                        open={openMsgMenuId === msg.id}
                        onClose={() => {
                          if (openMsgMenuId === msg.id) setOpenMsgMenuId(null);
                        }}
                        title={
                          <Box
                            display='flex'
                            flexDirection='column'
                            alignItems='flex-start'
                            style={{ padding: '4px 8px' }}
                          >
                            {canEditMessage(msg) && (
                              <Button
                                className={classes.menuAction}
                                startIcon={<CreateIcon style={{ fontSize: 16 }} />}
                                onClick={() => {
                                  setOpenMsgMenuId(null);
                                  setEditingMsgId(msg.id);
                                  setEditText(msg.content);
                                }}
                              >
                                <Typography>Edit</Typography>
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                className={classes.menuAction}
                                startIcon={<PinIconSmall />}
                                onClick={() => {
                                  setOpenMsgMenuId(null);
                                  handlePinMessage(msg.id);
                                }}
                              >
                                <Typography>{msg.isPinned ? 'Unpin' : 'Pin'}</Typography>
                              </Button>
                            )}
                            <Button
                              className={classes.menuActionDanger}
                              startIcon={<DeleteIcon style={{ fontSize: 16 }} />}
                              onClick={() => {
                                setOpenMsgMenuId(null);
                                handleDeleteMessage(msg.id);
                              }}
                            >
                              <Typography>Delete</Typography>
                            </Button>
                          </Box>
                        }
                      >
                        <IconButton
                          size='small'
                          disableRipple
                          className={classes.msgActionBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMsgMenuId((curr) => (curr === msg.id ? null : msg.id));
                          }}
                        >
                          <MoreVert style={{ fontSize: 18 }} />
                        </IconButton>
                      </TooltipMenu>
                      <ReactionPickerButton
                        className={classes.msgActionBtn}
                        onToggle={(emoji) => handleToggleReaction(msg.id, emoji)}
                      />
                    </Box>
                  )}
                  <Box style={{ position: 'relative' }}>
                    <Box
                      className={
                        [
                          'photo',
                          'activity_link',
                          'roster_link',
                          'checkin_link',
                          'cico_link',
                          'resource_link',
                          'report_share',
                        ].includes(msg.metadata?.type)
                          ? undefined
                          : bubbleClass
                      }
                    >
                      {msg.metadata?.type ? (
                        <>
                          <RichMessageCard
                            metadata={msg.metadata}
                            isOwn={isOwn}
                            messageId={msg.id}
                            conversationId={msg.conversationId}
                            onVote={handleVote}
                          />
                          {msg.content &&
                            ['photo', 'file'].includes(msg.metadata?.type) &&
                            !['Sent a photo', 'Sent a file'].includes(msg.content) && (
                              <Box className={bubbleClass} style={{ marginTop: 4 }}>
                                <Typography variant='body2' style={{ whiteSpace: 'pre-wrap' }}>
                                  {linkify(msg.content, isOwn)}
                                </Typography>
                              </Box>
                            )}
                        </>
                      ) : editingMsgId === msg.id ? (
                        <Box style={{ minWidth: 200 }}>
                          <TextField
                            fullWidth
                            multiline
                            autoFocus
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleEditMessage(msg.id);
                              }
                              if (e.key === 'Escape') {
                                setEditingMsgId(null);
                                setEditText('');
                              }
                            }}
                            InputProps={{
                              style: {
                                fontSize: '0.875rem',
                                color: isOwn ? '#fff' : '#333',
                                padding: 0,
                              },
                              disableUnderline: true,
                            }}
                          />
                          <Typography
                            variant='caption'
                            style={{ color: isOwn ? 'rgba(255,255,255,0.6)' : '#999', fontSize: '0.65rem' }}
                          >
                            Enter to save · Esc to cancel
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant='body2' style={{ whiteSpace: 'pre-wrap' }}>
                            {linkify(msg.content, isOwn)}
                          </Typography>
                          {msg.isEdited && (
                            <Typography
                              variant='caption'
                              style={{ color: isOwn ? 'rgba(255,255,255,0.5)' : '#999', fontSize: '0.6rem' }}
                            >
                              (edited)
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    <ReactionChips
                      reactions={msg.reactions}
                      currentUserId={currentUserId}
                      onToggle={(emoji) => handleToggleReaction(msg.id, emoji)}
                    />
                  </Box>
                  {!isOwn && (
                    <Box
                      className='msg-actions'
                      style={{
                        flexShrink: 0,
                        alignSelf: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        ...(openMsgMenuId === msg.id ? { opacity: 1 } : {}),
                      }}
                    >
                      <ReactionPickerButton
                        className={classes.msgActionBtn}
                        onToggle={(emoji) => handleToggleReaction(msg.id, emoji)}
                      />
                      {isAdmin && (
                        <TooltipMenu
                          open={openMsgMenuId === msg.id}
                          onClose={() => {
                            if (openMsgMenuId === msg.id) setOpenMsgMenuId(null);
                          }}
                          title={
                            <Box
                              display='flex'
                              flexDirection='column'
                              alignItems='flex-start'
                              style={{ padding: '4px 8px' }}
                            >
                              {isAdmin && (
                                <Button
                                  className={classes.menuAction}
                                  startIcon={<PinIconSmall />}
                                  onClick={() => {
                                    setOpenMsgMenuId(null);
                                    handlePinMessage(msg.id);
                                  }}
                                >
                                  <Typography>{msg.isPinned ? 'Unpin' : 'Pin'}</Typography>
                                </Button>
                              )}
                              <Button
                                className={classes.menuActionDanger}
                                startIcon={<DeleteIcon style={{ fontSize: 16 }} />}
                                onClick={() => {
                                  setOpenMsgMenuId(null);
                                  handleDeleteMessage(msg.id);
                                }}
                              >
                                <Typography>Delete</Typography>
                              </Button>
                            </Box>
                          }
                        >
                          <IconButton
                            size='small'
                            disableRipple
                            className={classes.msgActionBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMsgMenuId((curr) => (curr === msg.id ? null : msg.id));
                            }}
                          >
                            <MoreVert style={{ fontSize: 18 }} />
                          </IconButton>
                        </TooltipMenu>
                      )}
                    </Box>
                  )}
                </Box>
                {showTime && (
                  <Typography
                    variant='caption'
                    style={{
                      opacity: 0.5,
                      display: 'block',
                      marginTop: 2,
                      fontSize: '0.65rem',
                      textAlign: isOwn ? 'right' : 'left',
                      paddingLeft: isOwn ? 0 : 40,
                      paddingRight: isOwn ? 4 : 0,
                    }}
                  >
                    {formatMessageTime(msg.createdAt)}
                  </Typography>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='calc(100vh - 56px)'>
        <CircularProgress />
      </Box>
    );
  }

  const conversationList = (
    <Box className={isMobile ? classes.convListMobile : classes.convList}>
      <Box className={classes.searchBox}>
        <Box display='flex' gap={1} alignItems='center' style={{ gap: 8 }}>
          <TextField
            fullWidth
            size='small'
            variant='outlined'
            placeholder='Search conversations...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              style: { borderRadius: 30 },
              startAdornment: (
                <InputAdornment position='start'>
                  <Search style={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          {isAdmin && (
            <IconButton size='small' onClick={() => setShowCreateChannel(true)} style={{ color: '#7c4dff' }}>
              <Add />
            </IconButton>
          )}
        </Box>
      </Box>
      <List style={{ flex: 1, overflow: 'auto', padding: 0 }}>
        {filteredConversations.length === 0 && (
          <Box p={3} textAlign='center' color='#999'>
            <Typography variant='body2'>No conversations yet</Typography>
          </Box>
        )}
        {filteredConversations.map((conv) => {
          const lastMsgSender = conv.lastMessage ? profileCache[conv.lastMessage.volunteerId] : undefined;
          const lastMsgPreview = conv.lastMessage
            ? `${conv.lastMessage.volunteerId === currentUserId ? 'You' : lastMsgSender?.preferredName || ''}: ${
                conv.lastMessage.content
              }`
            : 'No messages yet';

          return (
            <ListItem
              key={conv.id}
              className={activeConversation?.id === conv.id ? classes.convItemActive : classes.convItem}
              onClick={() => {
                setActiveConversation(conv);
                setViewPinned(false);
              }}
            >
              <ListItemAvatar>
                <FallbackAvatar initials={getInitials(conv.name)} size={44} />
              </ListItemAvatar>
              <ListItemText
                primary={conv.name}
                secondary={lastMsgPreview}
                primaryTypographyProps={{ noWrap: true, style: { fontWeight: 500, fontSize: '0.9rem' } }}
                secondaryTypographyProps={{ noWrap: true, style: { fontSize: '0.8rem' } }}
              />
              <Box display='flex' flexDirection='column' alignItems='center' ml={1} style={{ minWidth: 40 }}>
                {conv.lastMessage && (
                  <Typography variant='caption' style={{ color: '#999', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    {formatTime(conv.lastMessage.createdAt)}
                  </Typography>
                )}
                {conv.unreadCount > 0 && (
                  <Box
                    style={{
                      background: '#7c4dff',
                      color: '#fff',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: 10,
                      padding: '1px 6px',
                      marginTop: 4,
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {conv.unreadCount}
                  </Box>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>
      {isAdmin && (
        <Box
          display='flex'
          justifyContent='center'
          style={{ borderTop: '1px solid #e0e0e0', padding: '14px 20px 22px' }}
        >
          <Button
            variant='outlined'
            startIcon={<VisibilityOutlined />}
            style={{
              height: 36,
              borderRadius: 6,
              border: '1px solid #DCDCDC',
              color: '#545454',
              textTransform: 'capitalize',
            }}
            onClick={() => setViewAllChannels(!viewAllChannels)}
          >
            <Typography style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>
              {viewAllChannels ? 'View: All' : 'View: Mine'}
            </Typography>
          </Button>
        </Box>
      )}
    </Box>
  );

  if (isMobile && activeConversation) {
    return (
      <Box display='flex' flexDirection='column' height='calc(100vh - 56px)'>
        <Box className={classes.msgHeader}>
          <Typography
            style={{ cursor: 'pointer', marginRight: 8, fontSize: '1.2rem', color: '#1976d2' }}
            onClick={() => setActiveConversation(null)}
          >
            &larr;
          </Typography>
          <MessageAvatar profile={profileCache[activeConversation.id]} size={36} />
          <Box style={{ flex: 1, paddingLeft: 12 }}>
            <Box display='flex' alignItems='center' style={{ gap: 6 }}>
              <Typography style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.2 }}>
                {activeConversation.name}
              </Typography>
              {activeConversation.isBroadcast && (
                <Box
                  display='inline-flex'
                  alignItems='center'
                  style={{
                    gap: 4,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: '#FFF4E0',
                    color: '#B26A00',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  <MegaphoneIcon style={{ fontSize: 12 }} />
                  Broadcast
                </Box>
              )}
            </Box>
            <Typography variant='caption' color='textSecondary' style={{ lineHeight: 1.2 }}>
              {activeConversation.participantCount} members
            </Typography>
          </Box>
          <Button
            size='small'
            onClick={() => setViewPinned(!viewPinned)}
            style={{
              marginRight: 8,
              borderRadius: 6,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              padding: '4px 10px',
              ...(viewPinned ? { background: '#616161', color: '#fff' } : { border: '1px solid #ccc', color: '#666' }),
            }}
          >
            View Pinned
          </Button>
          <ChannelOptionsMenu
            classes={classes}
            conversation={activeConversation}
            isAdmin={!!isAdmin}
            apiUrl={apiUrl}
            userToken={user?.token}
            chatToken={chatToken}
            onLeave={() => {
              setActiveConversation(null);
              chatFetch('/api/conversations').then((data) => {
                if (data) setConversations(data);
              });
            }}
            onUpdate={() => {
              chatFetch('/api/conversations').then((data) => {
                if (data) setConversations(data);
              });
            }}
          />
        </Box>

        {renderMessages()}

        {activeConversation?.isBroadcast && !isAdmin ? (
          <Box
            style={{
              padding: '14px 20px',
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
              color: '#666',
              fontSize: '0.85rem',
            }}
          >
            Only admins can post in this broadcast channel
          </Box>
        ) : (
          <>
            {renderPendingAttachment()}
            <Box className={classes.inputArea}>
              <MessageTypeMenu
                isAdmin={!!isAdmin}
                menuActionClass={classes.menuAction}
                onSelect={handleMessageTypeSelect}
              >
                <IconButton size='small' style={{ color: '#999' }}>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
                    <path d='M4.929 4.929a10 10 0 1 1 14.142 14.142a10 10 0 0 1 -14.142 -14.142zm8.071 4.071a1 1 0 1 0 -2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0 -2h-2v-2z' />
                  </svg>
                </IconButton>
              </MessageTypeMenu>
              <IconButton size='small' style={{ color: '#999', marginRight: 4 }} onClick={handlePhotoClick}>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2' />
                  <path d='M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0' />
                </svg>
              </IconButton>
              <IconButton size='small' style={{ color: '#999', marginRight: 4 }} onClick={handleFileAttachClick}>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5' />
                </svg>
              </IconButton>
              <TextField
                fullWidth
                size='small'
                variant='outlined'
                placeholder='Type a message...'
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                maxRows={4}
                InputProps={{ style: { borderRadius: 24, padding: '8px 16px' } }}
              />
              <IconButton
                className={classes.sendButton}
                onClick={handleSend}
                disabled={!inputText.trim() && !pendingPhoto && !pendingFile}
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{ display: 'block' }}
                >
                  <path d='M4.698 4.034l16.302 7.966l-16.302 7.966a.503 .503 0 0 1 -.546 -.124a.555 .555 0 0 1 -.12 -.568l2.468 -7.274l-2.468 -7.274a.555 .555 0 0 1 .12 -.568a.503 .503 0 0 1 .546 -.124' />
                  <path d='M6.5 12h14.5' />
                </svg>
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box display='flex' flexDirection='column' height='calc(100vh - 56px)'>
        {conversationList}
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {conversationList}

      {activeConversation ? (
        <Box className={classes.msgArea}>
          <Box className={classes.msgHeader}>
            <FallbackAvatar initials={getInitials(activeConversation.name)} size={36} />
            <Box style={{ flex: 1, paddingLeft: 10 }}>
              <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                <Typography style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.2 }}>
                  {activeConversation.name}
                </Typography>
                {activeConversation.isBroadcast && (
                  <Box
                    display='inline-flex'
                    alignItems='center'
                    style={{
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: '#FFF4E0',
                      color: '#B26A00',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    <MegaphoneIcon style={{ fontSize: 12 }} />
                    Broadcast
                  </Box>
                )}
              </Box>
              <Typography variant='caption' color='textSecondary' style={{ lineHeight: 1.2 }}>
                {activeConversation.participantCount} members
              </Typography>
            </Box>
            <Button
              size='small'
              startIcon={<PinIconSmall />}
              onClick={() => setViewPinned(!viewPinned)}
              style={{
                marginRight: 8,
                borderRadius: 6,
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: '4px 12px',
                ...(viewPinned
                  ? { background: '#616161', color: '#fff' }
                  : { border: '1px solid #ccc', color: '#666' }),
              }}
            >
              View Pinned
            </Button>
            <ChannelOptionsMenu
              classes={classes}
              conversation={activeConversation}
              isAdmin={!!isAdmin}
              apiUrl={apiUrl}
              userToken={user?.token}
              chatToken={chatToken}
              onLeave={() => {
                setActiveConversation(null);
                chatFetch('/api/conversations').then((data) => {
                  if (data) setConversations(data);
                });
              }}
              onUpdate={() => {
                chatFetch('/api/conversations').then((data) => {
                  if (data) setConversations(data);
                });
              }}
            />
          </Box>

          {renderMessages()}

          {activeConversation?.isBroadcast && !isAdmin ? (
            <Box
              style={{
                padding: '14px 20px',
                borderTop: '1px solid #e0e0e0',
                textAlign: 'center',
                color: '#666',
                fontSize: '0.85rem',
              }}
            >
              Only admins can post in this broadcast channel
            </Box>
          ) : (
            <>
              {renderPendingAttachment()}
              <Box className={classes.inputArea}>
                <MessageTypeMenu
                  isAdmin={!!isAdmin}
                  menuActionClass={classes.menuAction}
                  onSelect={handleMessageTypeSelect}
                >
                  <IconButton size='small' style={{ color: '#999' }}>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M4.929 4.929a10 10 0 1 1 14.142 14.142a10 10 0 0 1 -14.142 -14.142zm8.071 4.071a1 1 0 1 0 -2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0 -2h-2v-2z' />
                    </svg>
                  </IconButton>
                </MessageTypeMenu>
                <IconButton size='small' style={{ color: '#999', marginRight: 4 }} onClick={handlePhotoClick}>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2' />
                    <path d='M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0' />
                  </svg>
                </IconButton>
                <IconButton size='small' style={{ color: '#999', marginRight: 4 }} onClick={handleFileAttachClick}>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5' />
                  </svg>
                </IconButton>
                <TextField
                  fullWidth
                  size='small'
                  variant='outlined'
                  placeholder='Type a message...'
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  multiline
                  maxRows={4}
                  InputProps={{ style: { borderRadius: 24, padding: '8px 16px' } }}
                />
                <IconButton
                  className={classes.sendButton}
                  onClick={handleSend}
                  disabled={!inputText.trim() && !pendingPhoto && !pendingFile}
                >
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ display: 'block' }}
                  >
                    <path d='M4.698 4.034l16.302 7.966l-16.302 7.966a.503 .503 0 0 1 -.546 -.124a.555 .555 0 0 1 -.12 -.568l2.468 -7.274l-2.468 -7.274a.555 .555 0 0 1 .12 -.568a.503 .503 0 0 1 .546 -.124' />
                    <path d='M6.5 12h14.5' />
                  </svg>
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box className={classes.emptyState}>
          <Typography variant='h6' style={{ color: '#666' }}>
            Select a conversation
          </Typography>
          <Typography variant='body2'>Choose a conversation from the list to start chatting</Typography>
        </Box>
      )}

      <CreateChannelDialog
        open={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        onCreated={() => {
          chatFetch('/api/conversations').then((data) => {
            if (data) setConversations(data);
          });
        }}
      />

      <PollDialog
        open={activeMessageType === 'poll'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <ShareActivityDialog
        open={activeMessageType === 'activity_link'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <ShareLocationDialog
        open={activeMessageType === 'location_share'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <ImpactShareDialog
        open={activeMessageType === 'impact_share'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <ShareRosterCheckinDialog
        open={
          activeMessageType === 'roster_link' ||
          activeMessageType === 'checkin_link' ||
          activeMessageType === 'cico_link' ||
          activeMessageType === 'report_share'
        }
        type={(activeMessageType as 'roster_link' | 'checkin_link' | 'cico_link' | 'report_share') || 'roster_link'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <ShareResourceDialog
        open={activeMessageType === 'resource_link'}
        onClose={() => setActiveMessageType(null)}
        onSubmit={handleRichMessageSend}
      />

      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept='image/jpeg,image/png,image/gif,image/heic,image/heif'
        onChange={handleFileSelected}
      />
      <input
        type='file'
        ref={fileAttachInputRef}
        style={{ display: 'none' }}
        accept='.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.ppt,.pptx'
        onChange={handleFileAttachSelected}
      />
    </Box>
  );
};
